// src/pages/StaffManagement.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Divider,
  Tooltip,
  Badge,
  ConfigProvider,
  Tag as AntTag,
} from "antd";
import {
  listStaffUsers,
  createStaffUser,
  updateStaffUser,
  deleteStaffUser,
} from "../../api/userApi";
import "../style/theme-overrides.css";

// ---- Brand colors (tweak these to your exact codes) ----
const BRAND = {
  primary: "#FFA64D", // button fill (screenshot orange)
  primaryHover: "#FF9226", // hover
  primaryActive: "#F5821F", // active/pressed
  light50: "#FFF4E6", // soft bg for selected pills
  light100: "#FFE6C7", // hover bg for pills
  textOnBrand: "#ffffff",
};

/** ========= Permission dictionary (module -> {en, ar}) =========
 * Note: We keep bilingual labels here, but we ALWAYS display with t()
 * using the English key so translations come from Lang.json.
 */
const PERM_ALIAS = {
  lowStock: "stock",
};
const mapModuleToPerm = (module) => PERM_ALIAS[module] || module;

// Normalize any stored codes (e.g., legacy "lowStock:read" -> "stock:read")
const normalizePermCodes = (arr = []) =>
  arr.map((code) => {
    const [m, a] = String(code).split(":");
    return `${mapModuleToPerm(m)}:${a || "read"}`;
  });

const PERMISSION_MODULES = {
  // Inventory / Products
  products: { en: "Products", ar: "المنتجات" },
  brands: { en: "Brands", ar: "العلامات التجارية" },
  categories: { en: "Categories", ar: "الفئات" },
  subcategories: { en: "Sub Categories", ar: "الفئات الفرعية" },
  units: { en: "Units", ar: "الوحدات" },
  warehouses: { en: "Warehouses", ar: "المستودعات" },
  attributes: { en: "Attributes", ar: "السمات" },
  variantAttributes: { en: "Variant Attributes", ar: "سمات المتغيرات" },

  // Parties
  suppliers: { en: "Suppliers", ar: "الموردون" },
  customers: { en: "Customers", ar: "العملاء" },
  billers: { en: "Billers", ar: "المفوترون" },

  // Stock & Inventory Ops
  stockAdjustments: { en: "Stock Adjustments", ar: "تسويات المخزون" },
  stockTransfers: { en: "Stock Transfers", ar: "تحويلات المخزون" },
  inventoryLogs: { en: "Inventory Logs", ar: "سجلات الجرد" },
  lowStock: { en: "Low Stock", ar: "المخزون المنخفض" },
  outOfStock: { en: "Out of Stock", ar: "غير متوفر بالمخزون" },
  usage: { en: "Usage", ar: "الاستهلاك" },
  expire: { en: "Expiry", ar: "منتهي الصلاحية" },
  internal: { en: "Internal", ar: "داخلي" },

  // Sales & Orders & Money
  orders: { en: "Orders", ar: "الطلبات" },
  salesInvoices: { en: "Sales Invoices", ar: "فواتير المبيعات" },
  onlineOrders: { en: "Online Orders", ar: "الطلبات عبر الإنترنت" },
  transactions: { en: "Transactions", ar: "المعاملات" },

  // Organization / Setup
  stores: { en: "Stores", ar: "المتاجر" },
  warranties: { en: "Warranties", ar: "الضمانات" },

  // Reports (new granular report permissions)
  inventoryLogsReport: {
    en: "Inventory Logs Report",
    ar: "تقرير سجلات الجرد",
  },
  ordersReport: {
    en: "Orders Report",
    ar: "تقرير الطلبات",
  },

  // Special (usually read-only)
  dashboard: { en: "Dashboard", ar: "لوحة التحكم" },
  reports: { en: "Reports", ar: "التقارير" },
};

const MODULE_KEYS = Object.keys(PERMISSION_MODULES);
const READ_WRITE_MODULES = MODULE_KEYS.filter(
  (m) =>
    m !== "dashboard" &&
    m !== "reports" &&
    m !== "inventoryLogsReport" &&
    m !== "ordersReport"
);

// Action labels used inside perm labels; we’ll still run through t("Read")/t("Write")
const ACTION_LABELS = {
  read: { en: "Read", ar: "قراءة" },
  write: { en: "Write", ar: "كتابة" },
};

// Simple language detector used *only* to infer ar/en for direction-sensitive things if needed
// const currentLang = () => {
//   const toStr = (v) => (v ?? "").toString().toLowerCase();
//   const candidates = [
//     window?.lang,
//     window?.language,
//     window?.i18n?.language,
//     document?.documentElement?.lang,
//     navigator?.language,
//     ...(navigator?.languages || []),
//     document?.documentElement?.dir === "rtl" ? "ar" : "",
//   ]
//     .map(toStr)
//     .filter(Boolean);

//   const isAr = candidates.some((c) => c.startsWith("ar"));
//   return isAr ? "ar" : "en";
// };

// Translation helper (already in your codebase)
const t = (s) => (window.translate ? window.translate(s) : s);

// Helpers for localized text stored as {en, ar} objects
const toText = (v) =>
  typeof v === "object" && v ? v.en || v.ar || "" : v || "";
const normalizeLoc = (v, fallback = "") => ({
  en: (v ?? fallback) || "",
  ar: (v ?? fallback) || "",
});

// Permission label builder (now fully using t() instead of inline lang checks)
const permLabel = (permCode) => {
  const [module, action] = String(permCode).split(":");

  // Friendly label for stock:* used by Low Stock feature
  if (module === "stock") {
    const actionName = t(ACTION_LABELS[action]?.en || action);
    return `${t(PERMISSION_MODULES.lowStock.en)} — ${actionName}`;
  }

  if (module === "users" && action === "manage") return t("Users — Manage");
  if (module === "dashboard" && action === "read") return t("Dashboard — Read");
  if (module === "reports" && action === "read") return t("Reports — Read");

  const moduleName = t(PERMISSION_MODULES[module]?.en || module);
  const actionName = t(ACTION_LABELS[action]?.en || action);
  return `${moduleName} — ${actionName}`;
};

// Role select options — we’ll translate the label at render time
const ROLE_OPTIONS = [
  { label: "staff", value: "staff" },
  { label: "demo", value: "demo" },
];

/** ======== Pretty “Permission Grid” =========
 * Use title keys  t() so we don't need inline lang conditionals.
 */
const GROUPS = [
  {
    titleKey: "Inventory",
    modules: [
      "products",
      "brands",
      "categories",
      "subcategories",
      "units",
      "warehouses",
      "attributes",
      "variantAttributes",
    ],
  },
  { titleKey: "Parties", modules: ["suppliers", "customers", "billers"] },
  {
    titleKey: "Stock Operations",
    modules: [
      "stockAdjustments",
      "stockTransfers",
      "inventoryLogs",
      "lowStock",
      "outOfStock",
      "usage",
      "expire",
      "internal",
    ],
  },
  {
    titleKey: "Sales & Money",
    modules: ["orders", "salesInvoices", "onlineOrders", "transactions"],
  },
  { titleKey: "Setup", modules: ["stores", "warranties"] },
  {
    titleKey: "Special",
    modules: ["dashboard", "reports", "inventoryLogsReport", "ordersReport"],
  }, // read-only
];

function Pill({ active, children, onClick, type = "read" }) {
  return (
    <AntTag.CheckableTag
      checked={active}
      onChange={onClick}
      className="mb-1"
      style={{ borderRadius: 999, padding: "4px 10px" }}
    >
      <Badge
        status={
          active ? (type === "write" ? "processing" : "success") : "default"
        }
      />
      <span
        className={`ms-1 ${
          active ? (type === "write" ? "text-primary" : "text-success") : ""
        }`}
      >
        {children}
      </span>
    </AntTag.CheckableTag>
  );
}
Pill.propTypes = {
  active: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["read", "write"]),
};
Pill.defaultProps = {
  type: "read",
};

function PermissionGrid({ form }) {
  const [q, setQ] = useState("");

  const getPerms = () => form.getFieldValue("permissions") || [];
  const setPerms = (arr) =>
    form.setFieldsValue({ permissions: Array.from(new Set(arr)) });

  const toggle = (module, action) => {
    const perms = new Set(getPerms());
    // const code = `${module}:${action}`;
    const code = `${mapModuleToPerm(module)}:${action}`;
    if (perms.has(code)) {
      perms.delete(code);
      // if (action === "read" && perms.has(`${module}:write`))
      //   perms.delete(`${module}:write`);
      const writeCode = `${mapModuleToPerm(module)}:write`;
      if (action === "read" && perms.has(writeCode)) perms.delete(writeCode);
    } else {
      perms.add(code);
      // if (action === "write") perms.add(`${module}:read`);
      if (action === "write") perms.add(`${mapModuleToPerm(module)}:read`);
    }
    setPerms([...perms]);
  };

  const addMany = (codes) => setPerms([...new Set([...getPerms(), ...codes])]);
  const removeAll = () => setPerms([]);

  // Presets
  const selectAll = () => {
    const all = [
      ...READ_WRITE_MODULES.flatMap((m) => {
        const mm = mapModuleToPerm(m);
        return [`${mm}:read`, `${mm}:write`];
      }),
      "dashboard:read",
      "reports:read",
      "inventoryLogsReport:read",
      "ordersReport:read",
      "users:manage",
    ];
    setPerms(all);
  };
  const presetViewOnly = () => {
    const view = [
      ...READ_WRITE_MODULES.map((m) => `${mapModuleToPerm(m)}:read`),
      "dashboard:read",
      "reports:read",
      "inventoryLogsReport:read",
      "ordersReport:read",
    ];
    setPerms(view);
  };
  const presetInventoryAdmin = () => {
    const inv = [
      "products",
      "brands",
      "categories",
      "subcategories",
      "units",
      "warehouses",
      "attributes",
      "variantAttributes",
      "stockAdjustments",
      "stockTransfers",
      "inventoryLogs",
      "lowStock",
    ].flatMap((m) => {
      const mm = mapModuleToPerm(m);
      return [`${mm}:read`, `${mm}:write`];
    });
    addMany(inv);
  };
  const presetSalesClerk = () => {
    const sales = [
      "orders",
      "salesInvoices",
      "onlineOrders",
      "customers",
      "billers",
    ].flatMap((m) => {
      const mm = mapModuleToPerm(m);
      return [`${mm}:read`, `${mm}:write`];
    });
    addMany(sales);
  };

  const perms = getPerms();

  // Search in the localized label (via t())
  const match = (m) => {
    if (!q.trim()) return true;
    const hay = t(PERMISSION_MODULES[m]?.en || m).toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  };

  const countSelected = perms.length;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: BRAND.primary,
          colorPrimaryHover: BRAND.primaryHover,
          colorPrimaryActive: BRAND.primaryActive,
        },
      }}
    >
      <div className="permission-grid">
        {/* Toolbar */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Input
              placeholder={t("Search modules...")}
              allowClear
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 260 }}
            />
            <Tooltip title={t("View-only")}>
              <Button onClick={presetViewOnly}>{t("View-only")}</Button>
            </Tooltip>
            <Tooltip title={t("Inventory Admin")}>
              <Button onClick={presetInventoryAdmin}>
                {t("Inventory Admin")}
              </Button>
            </Tooltip>
            <Tooltip title={t("Sales Clerk")}>
              <Button onClick={presetSalesClerk}>{t("Sales Clerk")}</Button>
            </Tooltip>
          </div>
          <div className="d-flex align-items-center gap-4">
            <Badge count={countSelected} style={{ backgroundColor: "#fe9f43" }}>
              <span className="px-2">{t("Selected")}</span>
            </Badge>
            <Button onClick={removeAll}>{t("Clear")}</Button>
            <Button type="primary" ghost onClick={selectAll}>
              {t("Select All")}
            </Button>
          </div>
        </div>

        {/* Groups */}
        <Row gutter={[16, 16]}>
          {GROUPS.map((g) => {
            const visibleModules = g.modules.filter(match);
            if (!visibleModules.length) return null;
            return (
              <Col xs={24} md={12} key={g.titleKey}>
                <Card
                  size="small"
                  className="h-100"
                  title={<span className="fw-semibold">{t(g.titleKey)}</span>}
                  bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
                >
                  <Row gutter={[12, 12]}>
                    {visibleModules.map((m) => {
                      const name = t(PERMISSION_MODULES[m]?.en || m);
                      const readOnly =
                        m === "dashboard" ||
                        m === "reports" ||
                        m === "inventoryLogsReport" ||
                        m === "ordersReport";
                      const hasRead = perms.includes(
                        `${mapModuleToPerm(m)}:read`
                      );
                      const hasWrite = perms.includes(
                        `${mapModuleToPerm(m)}:write`
                      );
                      return (
                        <Col xs={24} key={m}>
                          <div className="d-flex align-items-center justify-content-between bg-light p-2 px-3 rounded-2">
                            <div className="fw-medium">{name}</div>
                            <div className="d-flex align-items-center gap-2">
                              {/* Read pill */}
                              <Pill
                                active={hasRead}
                                onClick={() => toggle(m, "read")}
                                type="read"
                              >
                                {t("Read")}
                              </Pill>
                              {/* Write pill (hidden for read-only modules) */}
                              {!readOnly && (
                                <Pill
                                  active={hasWrite}
                                  onClick={() => toggle(m, "write")}
                                  type="write"
                                >
                                  {t("Write")}
                                </Pill>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Users — Manage (standalone) */}
        <Divider className="my-3" />
        <Card size="small" className="border-0 bg-body">
          <div className="d-flex align-items-center justify-content-between bg-light p-2 px-3 rounded-2">
            <div className="fw-medium">{t("Users")}</div>
            <Pill
              active={perms.includes("users:manage")}
              onClick={() => {
                const set = new Set(perms);
                set.has("users:manage")
                  ? set.delete("users:manage")
                  : set.add("users:manage");
                setPerms([...set]);
              }}
              type="write"
            >
              {t("Manage")}
            </Pill>
          </div>
        </Card>
      </div>
    </ConfigProvider>
  );
}
PermissionGrid.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
};

export default function StaffManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const res = await listStaffUsers();
    if (!res.success) {
      message.error(res.error || t("Failed to load users"));
      setRows([]);
    } else {
      setRows(Array.isArray(res.data) ? res.data : []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      username: "",
      phone: "",
      role: "staff",
      readOnly: false,
      active: true,
      permissions: [],
    });
    setModalOpen(true);
  };

  const openEdit = useCallback(
    (row) => {
      setEditing(row);
      form.resetFields();
      form.setFieldsValue({
        email: row.email || "",
        password: "",
        firstName: toText(row.firstName),
        lastName: toText(row.lastName),
        username: toText(row.username),
        phone: row.phone || "",
        role: row.role || "staff",
        readOnly: !!row.readOnly,
        active: !!row.active,
        permissions: Array.isArray(row.permissions)
          ? normalizePermCodes(row.permissions)
          : [],
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        email: values.email.trim(),
        ...(values.password?.trim()
          ? { password: values.password.trim() }
          : {}),
        firstName: normalizeLoc(values.firstName?.trim() || ""),
        lastName: normalizeLoc(values.lastName?.trim() || ""),
        username: normalizeLoc(
          (values.username || "").trim() ||
            (values.email || "").split("@")[0] ||
            ""
        ),
        phone: values.phone?.trim() || "",
        role: values.role,
        readOnly: !!values.readOnly,
        active: !!values.active,
        permissions: normalizePermCodes(values.permissions || []),
      };

      if (!editing) {
        if (!payload.password) {
          message.warning(t("Password is required for new users"));
          setSaving(false);
          return;
        }
        const res = await createStaffUser(payload);
        if (!res.success) throw new Error(res.error);
        message.success(t("User created"));
      } else {
        const res = await updateStaffUser(editing._id, payload);
        if (!res.success) throw new Error(res.error);
        message.success(t("User updated"));
      }

      closeModal();
      await fetchRows();
    } catch (err) {
      if (!err?.errorFields) {
        message.error(err.message || t("Save failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = useCallback(
    async (id) => {
      const res = await deleteStaffUser(id);
      if (!res.success) {
        message.error(res.error || t("Delete failed"));
      } else {
        message.success(t("User deleted"));
        fetchRows();
      }
    },
    [fetchRows]
  );

const columns = useMemo(
  () => [
    {
      title: t("Email"),
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: t("Name"),
      dataIndex: "name",
      render: (_, r) => `${toText(r.firstName)} ${toText(r.lastName)}`.trim(),
      sorter: (a, b) =>
        `${toText(a.firstName)} ${toText(a.lastName)}`
          .trim()
          .localeCompare(`${toText(b.firstName)} ${toText(b.lastName)}`.trim()),
    },
    {
      title: t("Username"),
      dataIndex: "username",
      render: (_, r) => toText(r.username),
      sorter: (a, b) => toText(a.username).localeCompare(toText(b.username)),
    },
    {
      title: t("Role"),
      dataIndex: "role",
      render: (role) => <Tag>{role}</Tag>,
      sorter: (a, b) => (a.role || "").localeCompare(b.role || ""),
    },
    {
      title: t("Read Only"),
      dataIndex: "readOnly",
      render: (val) => (val ? t("Yes") : t("No")),
      sorter: (a, b) => Number(!!a.readOnly) - Number(!!b.readOnly),
    },
    {
      title: t("Active"),
      dataIndex: "active",
      render: (val) => (
        <Tag color={val ? "green" : "default"}>
          {val ? t("Active") : t("Inactive")}
        </Tag>
      ),
      sorter: (a, b) => Number(!!a.active) - Number(!!b.active),
    },
    {
      title: t("Permissions"),
      dataIndex: "permissions",
      render: (perms) =>
        Array.isArray(perms) && perms.length ? (
          <Space wrap>
            {perms.map((p) => (
              <Tag key={p}>{permLabel(p)}</Tag>
            ))}
          </Space>
        ) : (
          <span className="text-muted">—</span>
        ),
      sorter: (a, b) =>
        (a.permissions?.length || 0) - (b.permissions?.length || 0),
    },
    {
      title: t("Action"),
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            {t("Edit")}
          </Button>
          <Popconfirm
            title={t("Delete User")}
            description={t("Are you sure you want to delete this user?")}
            okText={t("Delete")}
            cancelText={t("Cancel")}
            onConfirm={() => onDelete(record._id)}
          >
            <Button size="small" danger>
              {t("Delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ],
  [openEdit, onDelete]
);


  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{t("Users, Roles & Permissions")}</h4>
              <h6>{t("Invite staff, edit access, and manage seats")}</h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/profile" className="btn btn-light border me-2">
              <i className="ti ti-arrow-left me-1" />
              {t("Back to Profile")}
            </Link>
            <Button
              type="primary"
              onClick={openCreate}
              style={{ backgroundColor: "#FE9F43", borderColor: "#FE9F43" }}
            >
              <i className="ti ti-user-plus me-1" />
              {t("Add User")}
            </Button>
          </div>
        </div>

        {/* Table card */}
        <div className="card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set" />
            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
              <span className="badge rounded-pill bg-warning-subtle text-warning border">
                {t("Seats are enforced by owner’s limit")}
              </span>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={rows}
                loading={loading}
                rowKey={(r) => r._id}
                pagination={{ pageSize: 10, showSizeChanger: true }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        title={editing ? t("Edit User") : t("Add User")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        onCancel={closeModal}
        onOk={onSave}
        destroyOnClose
        width={980}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{
            email: "",
            password: "",
            firstName: "",
            lastName: "",
            username: "",
            phone: "",
            role: "staff",
            readOnly: false,
            active: true,
            permissions: [],
          }}
        >
          <Form.Item
            label={t("Email")}
            name="email"
            rules={[
              { required: true, message: t("Email is required") },
              { type: "email", message: t("Enter a valid email") },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t("Password")}
            name="password"
            tooltip={editing ? t("Leave blank to keep current") : ""}
            rules={
              editing
                ? []
                : [{ required: true, message: t("Password is required") }]
            }
          >
            <Input.Password />
          </Form.Item>

          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              label={t("First Name")}
              name="firstName"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("Last Name")}
              name="lastName"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              label={t("Username")}
              name="username"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("Phone")}
              name="phone"
              style={{ flex: 1, minWidth: 220 }}
            >
              <Input />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              label={t("Role")}
              name="role"
              style={{ flex: 1, minWidth: 220 }}
              rules={[{ required: true, message: t("Role is required") }]}
            >
              <Select
                options={ROLE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(o.label),
                }))}
              />
            </Form.Item>
            <Form.Item
              label={t("Read Only")}
              name="readOnly"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label={t("Active")}
              name="active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>

          {/* New attractive permission grid */}
          <Form.Item label={t("Permissions")} name="permissions">
            <Form.Item noStyle shouldUpdate>
              {() => <PermissionGrid form={form} />}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
